'use client'

import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react'

declare global {
  interface Window { turnstile: any }
}

export type TurnstileHandle = { run: () => void; reset: () => void }

type TurnstileProps = {
  sitekey: string
  onVerify: (token: string) => void
  onError?: (err: any) => void
  onTimeout?: () => void
  onReady?: () => void
  autoRun?: boolean
}

const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(({ sitekey, onVerify, onError, onTimeout, onReady, autoRun }, ref) => {
  const widgetId = useRef<any>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!document.querySelector('#cf-turnstile-script')) {
      const script = document.createElement('script')
      script.id = 'cf-turnstile-script'
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
      script.async = true
      script.defer = true
      document.body.appendChild(script)
    }
  }, [])

  useEffect(() => {
    const check = setInterval(() => {
      if (!window.turnstile || !containerRef.current) return
      clearInterval(check)
      if (widgetId.current !== null) {
        try { window.turnstile.remove(widgetId.current) } catch {}
      }
      widgetId.current = window.turnstile.render(containerRef.current, {
        sitekey,
        // Cloudflare's current Turnstile API rejects size:'invisible' (throws
        // "Invalid value for parameter size" and the widget never renders, so
        // no token is ever issued). The modern equivalent of an invisible
        // widget is a normal-size widget with appearance:'interaction-only',
        // which stays hidden and only surfaces UI if a challenge needs it.
        appearance: 'interaction-only',
        execution: autoRun ? 'render' : 'execute',
        callback: (token: string) => onVerify(token),
        'error-callback': (err: any) => { console.log('[Turnstile] error:', err); onError?.(err) },
        'timeout-callback': () => { console.log('[Turnstile] timeout'); onTimeout?.() },
      })
      onReady?.()
    }, 100)
    return () => clearInterval(check)
  }, [sitekey, onVerify, onError, onTimeout, onReady, autoRun])

  useImperativeHandle(ref, () => ({
    run: () => { if (widgetId.current !== null) try { window.turnstile.execute(widgetId.current) } catch {} },
    reset: () => { if (widgetId.current !== null) try { window.turnstile.reset(widgetId.current) } catch {} },
  }))

  return <div ref={containerRef} />
})

Turnstile.displayName = 'Turnstile'
export default Turnstile
